/**
 * useTenantPlansV2 — Hooks da nova página /tenant/plans (arquitetura integracao_v1)
 *
 * Expõe:
 *  - useTenantPlansViewMode(): decide se a nova UI deve aparecer
 *  - useTenantEligiblePlansV1(): planos integracao_v1 + recorrências
 *  - useTenantEligibleAddonsV1(planId, recurrence): add-ons compatíveis
 *  - useTenantCheckoutCreateOrder(): mutation que grava intenção de compra
 *
 * Todas as chamadas passam pelas RPCs SECURITY DEFINER criadas em
 * `tenant_plans_v2_rpcs` (2026-06-14).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";
import { dispatchCobrancaToProvider } from "@/lib/financial/dispatchCobranca";
import { invalidateTenantBillingFreshness } from "@/hooks/tenant/useTenantBilling";

const log = createLogger("useTenantPlansV2");


// ---------------------------------------------------------------------------
// Tipagens compartilhadas
// ---------------------------------------------------------------------------
export type RecurrenceInterval =
  | "semanal"
  | "quinzenal"
  | "mensal"
  | "bimestral"
  | "trimestral"
  | "semestral"
  | "anual"
  | "bienal";

export interface PlanRecurrenceOption {
  recurrence_interval: RecurrenceInterval;
  price: number;
  setup_fee: number;
  discount_percent: number;
  /**
   * Desconto efetivamente praticado hoje para este tenant nesta recorrência
   * (vem do item vigente da assinatura). Sem item vigente, repete o
   * `discount_percent` de catálogo. Enviado pela RPC v1.7.0.
   */
  current_discount_percent?: number;
}

export interface EligiblePlanV1 {
  plan_id: string;
  name: string;
  description: string | null;
  billing_gateway_slug: string | null;
  max_inboxes: number;
  max_agents: number;
  max_channels: number;
  max_instances: number;
  max_crm_workspaces: number;
  max_tasks_month: number;
  max_tasks_storage_mb: number;
  // Limites informativos (sem enforcement).
  max_pipelines: number;
  max_webhooks: number;
  max_flows: number;
  max_contacts: number;
  has_crm: boolean;
  has_financial: boolean;
  has_tasks: boolean;
  has_referral: boolean;
  has_digital_contracts: boolean;
  has_campaigns: boolean;
  features: string[];
  // Meios de pagamento aceitos por este plano — UUIDs dos meios cadastrados
  // no Financeiro do tenant master.
  accepted_payment_methods: string[];
  // Lista resolvida (id+nome+tipo_pagamento) para renderizar os botões.
  accepted_payment_methods_resolved: Array<{
    id: string;
    nome: string;
    tipo_pagamento: string;
  }>;
  sort_order: number;
  is_current: boolean;
  recurrences: PlanRecurrenceOption[];
  /**
   * Recorrência atualmente contratada pelo tenant para este plano.
   * NULL quando o tenant não tem assinatura ativa neste plano.
   * Usado para hidratar o painel "Seu pedido" com o estado contratado.
   */
  current_recurrence_interval: RecurrenceInterval | null;
}

export interface EligibleAddonV1 {
  addon_id: string;
  slug: string;
  name: string;
  description: string | null;
  addon_type: string;
  unit_value: number | null;
  max_quantity: number | null;
  has_setup_fee: boolean;
  available_for_plan: boolean;
  price: number;
  setup_fee: number;
  discount_percent: number;
  sort_order: number;
  current_quantity: number;
  // Cotas específicas dos tiers de afiliados (referral_tier)
  max_referral_campaigns: number | null;
  max_referral_programs: number | null;
  max_referral_affiliates: number | null;
  referral_bonus_credits: number | null;
  // Modo de cobrança: 'recorrente' (preço por ciclo) ou 'avulso' (compra única).
  billing_mode: "recorrente" | "avulso";
  /**
   * Desconto praticado hoje para este tenant neste adicional (item vigente da
   * assinatura). Sem item vigente, repete o desconto de catálogo.
   * Enviado pela RPC v1.3.0.
   */
  current_discount_percent?: number;
}

// ---------------------------------------------------------------------------
// 1) View mode
// ---------------------------------------------------------------------------
export function useTenantPlansViewMode() {
  return useQuery({
    queryKey: ["tenant-plans-view-mode"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<"integracao_v1" | "legado"> => {
      const { data, error } = await supabase.rpc(
        "get_tenant_plans_view_mode" as never,
        {} as never,
      );
      if (error) {
        log.error("Erro view-mode", { error });
        throw error;
      }
      return (data as "integracao_v1" | "legado") ?? "legado";
    },
  });
}

// ---------------------------------------------------------------------------
// 2) Lista de planos elegíveis
// ---------------------------------------------------------------------------
export function useTenantEligiblePlansV1(enabled = true) {
  return useQuery({
    queryKey: ["tenant-eligible-plans-v1"],
    enabled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<EligiblePlanV1[]> => {
      const { data, error } = await supabase.rpc(
        "tenant_list_eligible_plans_v1" as never,
        {} as never,
      );
      if (error) {
        log.error("Erro listar planos V1", { error });
        throw error;
      }
      return ((data ?? []) as any[]).map((p) => ({
        ...p,
        features: Array.isArray(p.features) ? p.features : [],
        accepted_payment_methods: Array.isArray(p.accepted_payment_methods)
          ? (p.accepted_payment_methods as string[])
          : [],
        accepted_payment_methods_resolved: Array.isArray(
          p.accepted_payment_methods_resolved,
        )
          ? p.accepted_payment_methods_resolved
          : [],
        recurrences: Array.isArray(p.recurrences) ? p.recurrences : [],
      })) as EligiblePlanV1[];
    },
  });
}

// ---------------------------------------------------------------------------
// 3) Lista de add-ons elegíveis
// ---------------------------------------------------------------------------
export function useTenantEligibleAddonsV1(
  planId: string | null,
  recurrence: RecurrenceInterval | null,
) {
  return useQuery({
    queryKey: ["tenant-eligible-addons-v1", planId, recurrence],
    enabled: !!planId && !!recurrence,
    staleTime: 60 * 1000,
    // v1.1.0 — força refetch a cada montagem da página de Planos para não
    // servir baseline obsoleto ao carrinho (bug: add-on recém-pago aparecia
    // como remoção no ConfirmOrderDialog até um F5).
    refetchOnMount: "always",
    queryFn: async (): Promise<EligibleAddonV1[]> => {
      const { data, error } = await supabase.rpc(
        "tenant_list_eligible_addons_v1" as never,
        {
          p_plan_id: planId,
          p_recurrence_interval: recurrence,
        } as never,
      );
      if (error) {
        log.error("Erro listar add-ons V1", { error });
        throw error;
      }
      return (data ?? []) as EligibleAddonV1[];
    },
  });
}

// ---------------------------------------------------------------------------
// 4) Mutation de checkout
// ---------------------------------------------------------------------------
export interface CheckoutOrderPayload {
  plan_id: string;
  recurrence_interval: RecurrenceInterval;
  // UUID do meio de pagamento (financeiro_meios_pagamento.id)
  payment_method: string;
  addons: Array<{ addon_id: string; quantity: number }>;
}

/** Add-on cobrado de forma avulsa (compra única) devolvido pelo backend. */
export interface CheckoutAddonAvulso {
  addon_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  total: number;
}

/** Retorno da RPC tenant_checkout_create_order v2.0.0 (JSONB). */
export interface CheckoutOrderResult {
  binding_id: string;
  financeiro_assinatura_id: string;
  first_cobranca_id: string;
  first_cobranca: {
    cobranca_id: string;
    tipo_pagamento: string;
    valor_total: number;
    data_vencimento: string;
    descricao: string;
    numero_parcelas: number;
    tipo_cobranca: string;
  };
  master_tenant_id: string;
  crm_customer_id: string;
  conta_corrente_id: string;
  meio_pagamento_id: string;
  tipo_pagamento: string;
  valor_total: number;
  primeiro_vencimento: string;
  status: "pending" | "trial" | "active" | "past_due" | "canceled";
  trial_ends_at: string | null;
  addons_avulsos: CheckoutAddonAvulso[];
  /** v2.1.0: presente quando a RPC delegou para apply_changes (binding já existia). */
  change_scheduled?: boolean;
  effective_at?: string | null;
  pending_change?: Record<string, unknown>;
  /** v2.0.0 apply_changes — distinção pre_paid x post_paid. */
  change_applied_immediately?: boolean;
  requires_payment?: boolean;
  billing_type?: "pre_paid" | "post_paid";
  /**
   * v2.4.5 do tenant_subscription_apply_changes: presente quando a troca
   * imediata gerou cobrança de ajuste pro-rata pronta para despacho ao
   * provider. Frontend dispara via helper `dispatchCobrancaToProvider`.
   */
  dispatch_charge?: {
    cobranca_id: string;
    conta_corrente_id: string;
    crm_customer_id: string;
    valor_total: number | string;
    data_vencimento: string | null;
    descricao: string | null;
    tipo_pagamento: string;
    numero_parcelas?: number;
    observacoes?: string | null;
    origin?: string;
  } | null;
}



export function useTenantCheckoutCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CheckoutOrderPayload,
    ): Promise<CheckoutOrderResult> => {
      // 1) Cria a ordem no master (RPC v2.0.0 — retorna JSONB completo)
      const { data, error } = await supabase.rpc(
        "tenant_checkout_create_order" as never,
        { p_payload: payload } as never,
      );
      if (error) throw error;
      const result = data as unknown as CheckoutOrderResult;

      // 2) Se a RPC apenas registrou intenção (pré-pago aguardando pagamento
      //    ou agendamento legado), não há cobrança para emitir agora.
      if (result?.change_scheduled || result?.requires_payment) {
        return result;
      }

      // 3a) 1ª cobrança da contratação inicial — despacha via helper único
      //     compartilhado com o painel WL (sem duplicar lógica).
      if (result?.first_cobranca_id) {
        // v2.0.0 (2026-06-17): helper enxuto — backend carrega valor/vencimento/
        // descricao/tipo_pagamento direto do banco via cobranca_id. Elimina
        // qualquer chance de divergência local↔Asaas.
        await dispatchCobrancaToProvider({
          cobranca_id: result.first_cobranca_id,
          conta_corrente_id: result.conta_corrente_id,
          crm_customer_id: result.crm_customer_id,
          tipo_pagamento: result.tipo_pagamento,
          origin: "tenant_first_charge",
        });
      }

      // 3b) Troca de plano imediata (pós-pago ou WL): a RPC v2.4.5 expõe
      //     `dispatch_charge` com os dados da cobrança de ajuste pro-rata.
      //     Sem este passo a cobrança ficava em 'aguardando_emissao'.
      const dispatchCharge = result?.dispatch_charge;
      if (dispatchCharge?.cobranca_id) {
        await dispatchCobrancaToProvider({
          cobranca_id: dispatchCharge.cobranca_id,
          conta_corrente_id: dispatchCharge.conta_corrente_id,
          crm_customer_id: dispatchCharge.crm_customer_id,
          tipo_pagamento: dispatchCharge.tipo_pagamento ?? "pix",
          observacoes: dispatchCharge.observacoes ?? null,
          origin: dispatchCharge.origin ?? "tenant_plan_change_pro_rata",
        });
      }

      return result;

    },
    onSuccess: () => {
      // Invalida baseline, catálogo e cotas para refletir add-ons
      // cancelados/adicionados imediatamente em todas as abas dependentes.
      invalidateTenantBillingFreshness(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// 5) Resumo de billing do próprio tenant (PaymentGuard — Fase D)
// ---------------------------------------------------------------------------
export interface TenantBillingSelfOverview {
  has_binding: boolean;
  binding_id?: string;
  gateway_slug?: string | null;
  status:
    | "inactive"
    | "pending"
    | "trial"
    | "active"
    | "past_due"
    | "suspended"
    | "canceled";

  plan_name?: string | null;
  trial_ends_at?: string | null;

  current_period_end?: string | null;
  /**
   * v1.6.0 — Próxima data de cobrança gerada pela engine de recorrência
   * (`financeiro_assinaturas.proximo_vencimento`). Usado pelo hero/KPI
   * como "Próximo ciclo" no lugar do antigo `current_period_end`
   * (que reflete o fim do contrato, não o próximo evento de cobrança).
   */
  next_cycle_at?: string | null;
  days_past_due?: number;
  dunning_block_modules_after_days?: number;
  dunning_suspend_tenant_after_days?: number;
  dunning_days_left?: number;
  suspend_days_left?: number;
  last_cobranca?: {
    id: string;
    status: string;
    data_vencimento: string;
    valor_total: number;
    ciclo_numero: number;
  } | null;
  /**
   * Fatura em aberto (pendente/vencida/aguardando) já com os campos necessários
   * para resolver o link de pagamento via `resolveCheckoutUrl` (transparente x
   * provider). Exposto pela RPC v1.3.0+.
   */
  open_invoice?: {
    id: string;
    status: string;
    data_vencimento: string;
    valor_total: number;
    provider_payment_link: string | null;
    provider_invoice_url: string | null;
    checkout_session_slug: string | null;
    checkout_mode: "transparent" | "provider" | null;
    // v1.4.0 — campos para o fluxo "Pagar com cartão salvo" sem nova RPC.
    master_tenant_id?: string | null;
    conta_corrente_id?: string | null;
    crm_customer_id?: string | null;
    provider_charge_id?: string | null;
  } | null;
  /**
   * v1.10.0 do backend — Lista COMPLETA de cobranças em aberto da assinatura
   * (mesmos status de `open_invoice`: pendente/vencida/aguardando_pagamento/
   * em_aberto), ordenadas por vencimento. Usada pela aba "Planos e
   * adicionais" para exibir TODAS as faturas em aberto — antes só a mais
   * antiga era devolvida (`open_invoice`), o que escondia as demais.
   * Mantido aditivo: consumidores antigos continuam usando `open_invoice`.
   */
  open_invoices?: Array<{
    id: string;
    status: string;
    data_vencimento: string | null;
    valor_total: number;
    descricao?: string | null;
    is_prorated?: boolean | null;
    provider_payment_link: string | null;
    provider_invoice_url: string | null;
    checkout_session_slug: string | null;
    checkout_mode: "transparent" | "provider" | null;
    master_tenant_id?: string | null;
    conta_corrente_id?: string | null;
    crm_customer_id?: string | null;
    provider_charge_id?: string | null;
  }> | null;
  addons_count?: number;
  addons_total_value?: number;
  /**
   * v1.7.0 do backend — TRUE quando o tenant contratou um plano (binding existe)
   * mas ainda não teve nenhuma cobrança liquidada, o trial não está ativo e o
   * status do binding ≠ 'active'. Usado pelo PaymentGuard para bloquear o
   * acesso ao sistema na 1ª contratação pré-paga até que a primeira fatura
   * seja paga.
   */
  awaiting_first_payment?: boolean;
  /**
   * v1.8.0 do backend — Preenchido quando o usuário trocou de plano e a
   * cobrança pro-rata correspondente ainda não foi paga. Enquanto existir,
   * o plano vigente (subscription_plan_id) permanece o anterior — os
   * benefícios do novo plano só ficam disponíveis após a promoção
   * automática disparada pelo webhook de pagamento
   * (`promote_pending_plan_change`).
   */
  pending_plan_change?: {
    new_plan_id: string;
    new_plan_name: string | null;
    new_recurrence: string | null;
    previous_plan_id: string | null;
    previous_recurrence: string | null;
    cobranca_id: string;
    valor: number | null;
    vencimento: string | null;
    applied_at: string | null;
  } | null;

  /**
   * v1.9.0 do backend — Cancelamento AGENDADO (cancel_at_period_end).
   * Enquanto `cancel_scheduled` for TRUE e `cancel_effective_at` estiver no
   * futuro, o tenant mantém acesso normal, nenhuma cobrança nova é gerada e
   * a UI deve exibir um aviso com contagem regressiva + botão "Contratar
   * novo plano" (que reaproveita `onGoToCatalog`).
   */
  cancel_scheduled?: boolean;
  cancel_scheduled_at?: string | null;
  cancel_effective_at?: string | null;
  cancel_reason?: string | null;
}


export function useTenantBillingSelfOverview(enabled = true) {
  return useQuery({
    queryKey: ["tenant-billing-self-overview"],
    enabled,
    staleTime: 30 * 1000,
    // v1.1.0 — refetch obrigatório ao (re)montar a página de Planos,
    // garantindo status/valores atualizados após pagamento em outra aba.
    refetchOnMount: "always",
    queryFn: async (): Promise<TenantBillingSelfOverview> => {
      const { data, error } = await supabase.rpc(
        "tenant_billing_get_self_overview" as never,
        {} as never,
      );
      if (error) {
        log.error("Erro overview billing", { error });
        throw error;
      }
      return data as unknown as TenantBillingSelfOverview;
    },
  });
}

// ---------------------------------------------------------------------------
// 6) Cancelamento de assinatura (Fase E)
// ---------------------------------------------------------------------------
export interface CancelSubscriptionResult {
  /** v1.1.0 — cancelamento agora é AGENDADO: `scheduled=true` até `effective_at`. */
  scheduled?: boolean;
  /** Compat v1.0.0 — mantido para chamadas antigas ainda no cache. */
  canceled?: boolean;
  binding_id: string;
  /** Alias legado — igual a `effective_at`. */
  access_until: string | null;
  effective_at?: string | null;
  charges_cancelled?: number;
}

export function useTenantSubscriptionCancel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: { reason?: string } = {},
    ): Promise<CancelSubscriptionResult> => {
      const { data, error } = await supabase.rpc(
        "tenant_subscription_cancel" as never,
        { p_reason: payload.reason ?? null } as never,
      );
      if (error) {
        log.error("Erro ao cancelar assinatura", { error });
        throw error;
      }
      return data as unknown as CancelSubscriptionResult;
    },
    onSuccess: () => {
      // Cancelamento da assinatura impacta baseline, catálogo e entitlement.
      invalidateTenantBillingFreshness(qc);
    },
  });
}


